// The date row (decided 2026-10-12): calendar icon, "Oct 15", caret on the
// left. On the right, tonight shows a countdown to rollover in hh:mm:ss (the
// night turns at midnight UTC wherever the player is, which its hover says),
// and a past night an ARCHIVE tag in its place. The caret opens the calendar,
// the way to every night including tonight.
import { useEffect, useRef, useState } from 'react';
import { navigate, nightPath } from '../../app/router';
import type { NightResponse } from '../../systems/api/types';
import { formatCountdown, formatShortDate } from '../../systems/clock/serverClock';
import { Icon } from '../components/Icon';
import { Calendar } from './Calendar';
import { useServerNow } from './ResetTimer';

interface Props {
  night: NightResponse | null;
  /** The past night asked for, or null for tonight. */
  requested: string | null;
}

export function DateRow({ night, requested }: Props) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const date = requested ?? night?.date ?? null;

  // A tap outside the row and its calendar, or Escape, closes it.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => wrap.current && !wrap.current.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (picked: string) => {
    setOpen(false);
    if (night && picked !== night.date) navigate(nightPath(picked === night.tonight ? null : picked));
  };

  return (
    <div className="date-row-wrap" ref={wrap}>
      <div className="date-row">
        <button type="button" className="date-button" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="dialog">
          <Icon name="calendar" small />
          <span className="date-text">{date ? formatShortDate(date) : '—'}</span>
          <Icon name={open ? 'caret-up' : 'caret-down'} small className="quiet" />
        </button>
        {requested ? <span className="tag tag-archive">Archive</span> : <Countdown rolloverAt={night?.rolloverAt ?? null} />}
      </div>
      {open && night && <Calendar shown={night.date} tonight={night.tonight} firstNight={night.firstNight} onPick={pick} />}
    </div>
  );
}

function Countdown({ rolloverAt }: { rolloverAt: number | null }) {
  const now = useServerNow();
  if (rolloverAt === null) return <span className="countdown mono quiet">--:--:--</span>;
  return (
    <span className="countdown mono" title="Nightly puzzles reset at midnight UTC.">
      {formatCountdown(rolloverAt - now)}
    </span>
  );
}
